import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SelectContextType {
    value: string | string[];
    onValueChange: (value: string | string[]) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
    multiple?: boolean;
    disabled?: boolean;
}

interface SelectProps {
    value?: string | string[];
    defaultValue?: string | string[];
    onValueChange?: (value: string | string[]) => void;
    children: React.ReactNode;
    disabled?: boolean;
    multiple?: boolean;
}

interface SelectTriggerProps {
    children: React.ReactNode;
    className?: string;
    variant?: 'default' | 'filled' | 'ghost' | 'error';
    size?: 'sm' | 'default' | 'lg';
    placeholder?: string;
    error?: string;
    label?: string;
    helperText?: string;
    containerClassName?: string;
}

interface SelectContentProps {
    children: React.ReactNode;
    className?: string;
}

interface SelectItemProps {
    value: string;
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
}

interface SelectValueProps {
    placeholder?: string;
    className?: string;
}

// Context
const SelectContext = createContext<SelectContextType | null>(null);

const useSelectContext = () => {
    const context = useContext(SelectContext);
    if (!context) {
        throw new Error('Select components must be used within a Select');
    }
    return context;
};

// Main Select component
const Select: React.FC<SelectProps> = ({
                                           value,
                                           defaultValue,
                                           onValueChange,
                                           children,
                                           disabled = false,
                                           multiple = false
                                       }) => {
    const [internalValue, setInternalValue] = useState<string | string[]>(
        value || defaultValue || (multiple ? [] : '')
    );
    const [open, setOpen] = useState(false);

    const currentValue = value !== undefined ? value : internalValue;

    const handleValueChange = (newValue: string | string[]) => {
        if (value === undefined) {
            setInternalValue(newValue);
        }
        onValueChange?.(newValue);
    };

    useEffect(() => {
        if (value !== undefined) {
            setInternalValue(value);
        }
    }, [value]);

    return (
        <SelectContext.Provider
            value={{
                value: currentValue,
                onValueChange: handleValueChange,
                open,
                setOpen,
                multiple,
                disabled
            }}
        >
            <div className="relative">
                {children}
            </div>
        </SelectContext.Provider>
    );
};

// Select Trigger
const SelectTrigger: React.FC<SelectTriggerProps> = ({
                                                         children,
                                                         className,
                                                         variant = 'default',
                                                         size = 'default',
                                                         error,
                                                         label,
                                                         helperText,
                                                         containerClassName
                                                     }) => {
    const { open, setOpen, disabled } = useSelectContext();
    const triggerRef = useRef<HTMLButtonElement>(null);

    const hasError = !!error;

    // Variant styles matching Input component
    const variantStyles = {
        default: cn(
            "bg-background-light-secondary dark:bg-background-dark-secondary",
            "border border-border-light dark:border-border-dark-subtle",
            "focus:bg-background-light dark:focus:bg-background-dark-elevated",
            "focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
            "hover:border-border-light dark:hover:border-border-dark",
            open && "border-primary-500 ring-2 ring-primary-500"
        ),
        filled: cn(
            "bg-background-light-elevated dark:bg-background-dark-elevated",
            "border border-transparent",
            "focus:bg-background-light dark:focus:bg-background-dark-secondary",
            "focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
            "hover:bg-background-light-secondary dark:hover:bg-background-dark-secondary"
        ),
        ghost: cn(
            "bg-transparent",
            "border border-border-light dark:border-border-dark-subtle",
            "focus:bg-background-light-secondary dark:focus:bg-background-dark-secondary",
            "focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
            "hover:bg-background-light-secondary dark:hover:bg-background-dark-secondary"
        ),
        error: cn(
            "bg-background-light-secondary dark:bg-background-dark-secondary",
            "border border-red-300 dark:border-red-700",
            "focus:bg-background-light dark:focus:bg-background-dark-elevated",
            "focus:ring-2 focus:ring-red-500 focus:border-red-500"
        )
    };

    // Size styles
    const sizeStyles = {
        sm: "h-8 px-3 py-1 text-xs",
        default: "h-10 px-3 py-2 text-sm",
        lg: "h-12 px-4 py-3 text-base"
    };

    const iconSize = {
        sm: "h-3 w-3",
        default: "h-4 w-4",
        lg: "h-5 w-5"
    };

    return (
        <div className={cn("space-y-1", containerClassName)}>
            {label && (
                <label className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                    {label}
                </label>
            )}

            <button
                ref={triggerRef}
                type="button"
                onClick={() => !disabled && setOpen(!open)}
                className={cn(
                    "w-full flex items-center justify-between rounded-lg font-roboto transition-all duration-200",
                    "text-text-primary-light dark:text-text-primary-dark",
                    "focus:outline-none text-left",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    sizeStyles[size],
                    variantStyles[hasError ? 'error' : variant],
                    className
                )}
                disabled={disabled}
                aria-expanded={open}
            >
        <span className="flex-1 truncate">
          {children}
        </span>
                <ChevronDown
                    className={cn(
                        "ml-2 flex-shrink-0 text-text-muted-light dark:text-text-muted-dark transition-transform duration-200",
                        iconSize[size],
                        open && "rotate-180"
                    )}
                />
            </button>

            {(error || helperText) && (
                <div className="flex items-start space-x-1">
                    {error && (
                        <>
                            <AlertCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-500">{error}</p>
                        </>
                    )}
                    {!error && helperText && (
                        <p className="text-xs text-text-muted-light dark:text-text-muted-dark">{helperText}</p>
                    )}
                </div>
            )}
        </div>
    );
};

// Select Content
const SelectContent: React.FC<SelectContentProps> = ({ children, className }) => {
    const { open, setOpen } = useSelectContext();
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (contentRef.current && !contentRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [open, setOpen]);

    if (!open) return null;

    return (
        <div
            ref={contentRef}
            className={cn(
                "absolute z-50 w-full mt-1 rounded-lg border shadow-lg",
                "bg-background-light dark:bg-background-dark-secondary",
                "border-border-light dark:border-border-dark-subtle",
                "max-h-60 overflow-auto",
                "animate-in fade-in-0 zoom-in-95",
                className
            )}
        >
            <div className="p-1">
                {children}
            </div>
        </div>
    );
};

// Select Item
const SelectItem: React.FC<SelectItemProps> = ({
                                                   value,
                                                   children,
                                                   className,
                                                   disabled = false
                                               }) => {
    const { value: selectedValue, onValueChange, setOpen, multiple } = useSelectContext();

    const isSelected = multiple
        ? Array.isArray(selectedValue) && selectedValue.includes(value)
        : selectedValue === value;

    const handleSelect = () => {
        if (disabled) return;

        if (multiple) {
            const currentValues = Array.isArray(selectedValue) ? selectedValue : [];
            const newValues = isSelected
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            onValueChange(newValues);
        } else {
            onValueChange(value);
            setOpen(false);
        }
    };

    return (
        <div
            className={cn(
                "relative flex items-center w-full rounded-md px-3 py-2 text-sm cursor-pointer transition-colors",
                "text-text-primary-light dark:text-text-primary-dark",
                "hover:bg-background-light-elevated dark:hover:bg-background-dark-elevated",
                isSelected && "bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400",
                disabled && "opacity-50 cursor-not-allowed",
                className
            )}
            onClick={handleSelect}
        >
            <span className="flex-1 truncate">{children}</span>
            {isSelected && (
                <Check className="h-4 w-4 ml-2 flex-shrink-0 text-primary-500" />
            )}
        </div>
    );
};

// Select Value
const SelectValue: React.FC<SelectValueProps> = ({ placeholder, className }) => {
    const { value, multiple } = useSelectContext();

    const displayValue = () => {
        if (multiple && Array.isArray(value)) {
            if (value.length === 0) return placeholder;
            if (value.length === 1) return value[0];
            return `${value.length} items selected`;
        }

        return value || placeholder;
    };

    return (
        <span
            className={cn(
                "truncate",
                (!value || (Array.isArray(value) && value.length === 0)) &&
                "text-text-muted-light dark:text-text-muted-dark",
                className
            )}
        >
      {displayValue()}
    </span>
    );
};

export {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue
};